import type {
  SupportTicketApiAttachment,
  SupportTicketApiComment,
  SupportTicketApiItem,
  SupportTicketAttachment,
  SupportTicketComment,
  SupportTicketDetail,
  SupportTicketDetailApiResponse,
  SupportTicketListEntry,
  SupportTicketStatus,
} from "../supportTicketTypes";
import { formatSupportTicketTimestamp } from "./formatSupportTicketTimestamp";
import { mapSupportTicketCommentAuthorLabel } from "./isRisaTeamEmail";

/**
 * Maps backend ticket status labels to sidebar/detail tag values.
 *
 * @param apiStatus - Status string from the support tickets API.
 * @returns Normalized status used by the support tickets UI.
 */
export function mapSupportTicketStatus(
  apiStatus: string | null | undefined,
): SupportTicketStatus {
  const normalized = (apiStatus ?? "").trim().toLowerCase();

  if (normalized === "in progress" || normalized === "waiting for client") {
    return "in_progress";
  }

  if (normalized === "onshore assistance") {
    return "onshore_assistance";
  }

  if (normalized === "resolved") {
    return "resolved";
  }

  if (normalized === "closed" || normalized === "archived") {
    return "archived";
  }

  return "open";
}

/** API status labels sent when updating a support ticket from the UI. */
const SUPPORT_TICKET_STATUS_API_LABELS: Record<SupportTicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  onshore_assistance: "Onshore Assistance",
  resolved: "Resolved",
  archived: "Closed",
};

/**
 * Maps UI ticket status values to backend status labels.
 *
 * @param status - Normalized status used by the support tickets UI.
 * @returns Status label accepted by POST /update.
 */
export function mapSupportTicketStatusToApi(
  status: SupportTicketStatus,
): string {
  return SUPPORT_TICKET_STATUS_API_LABELS[status];
}

/**
 * Builds a single-letter avatar label from a comment author display name.
 *
 * @param authorName - Author display name shown in the support ticket thread.
 * @returns First letter of the display name, uppercased.
 */
function mapSupportTicketAvatarLabel(
  authorName: string | null | undefined,
): string {
  const trimmed = authorName?.trim() ?? "";
  if (!trimmed) {
    return "?";
  }

  return trimmed.charAt(0).toUpperCase();
}

/**
 * Formats a comment timestamp for the support ticket thread UI.
 *
 * @param isoTimestamp - ISO-8601 timestamp from the support tickets API.
 * @returns Locale-formatted date/time label.
 */
function formatSupportTicketCommentTimestamp(isoTimestamp: string): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return isoTimestamp;
  }

  return parsed.toLocaleString(undefined, {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Maps one API comment into the UI comment model.
 *
 * @param comment - Comment row from POST /get-details.
 * @returns Comment payload for the support ticket thread.
 */
function mapApiAttachment(
  attachment: SupportTicketApiAttachment,
): SupportTicketAttachment {
  return {
    id: attachment.id,
    filename: attachment.filename,
    gcsPath: attachment.gcs_path,
    contentType: attachment.content_type,
    size: attachment.size ?? 0,
  };
}

function mapSupportTicketComment(
  comment: SupportTicketApiComment,
): SupportTicketComment {
  const authorType = comment.author_type.trim().toLowerCase();
  const senderName = mapSupportTicketCommentAuthorLabel(
    comment.author_email,
    comment.author_name,
  );

  return {
    id: comment.id,
    senderName,
    timestamp: formatSupportTicketCommentTimestamp(comment.created_at),
    message: comment.message,
    side: authorType === "risa" ? "right" : "left",
    avatarLabel: mapSupportTicketAvatarLabel(senderName),
    attachments: (comment.attachments ?? []).map(mapApiAttachment),
  };
}

/**
 * Converts an ISO timestamp to epoch milliseconds for thread ordering.
 *
 * @param isoTimestamp - ISO-8601 timestamp from the support tickets API.
 * @returns Epoch milliseconds, or 0 when the timestamp is unparseable.
 */
function toEpochMs(isoTimestamp: string): number {
  const parsed = new Date(isoTimestamp).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Builds the comment thread ordered oldest-first.
 *
 * Only comments appear in the chat thread; files sent with a comment render
 * inside that comment's bubble. Ticket-scoped attachments (no comment_id) are
 * surfaced separately in the description area, not here.
 *
 * @param comments - Comment rows from POST /get-details.
 * @returns Time-sorted comment thread items.
 */
function buildSupportTicketThread(
  comments: SupportTicketApiComment[],
): SupportTicketComment[] {
  return comments
    .map((comment) => ({
      ts: toEpochMs(comment.created_at),
      item: mapSupportTicketComment(comment),
    }))
    .sort((a, b) => a.ts - b.ts)
    .map((entry) => entry.item);
}

/**
 * Converts one API ticket row into a sidebar list entry.
 *
 * @param ticket - Ticket row from POST /get-tickets.
 * @returns Sidebar list row with display timestamp.
 */
export function mapSupportTicketListEntry(
  ticket: SupportTicketApiItem,
): SupportTicketListEntry {
  return {
    id: ticket.id,
    title: ticket.title,
    timestamp: formatSupportTicketTimestamp(
      ticket.last_comment_at ?? ticket.updated_at ?? ticket.created_at,
    ),
    status: mapSupportTicketStatus(ticket.status),
    commentCount: ticket.comment_count ?? 0,
    attachmentCount: ticket.attachment_count ?? 0,
  };
}

/**
 * Maps POST /get-details response into the main content area model.
 *
 * @param ticket - Full ticket detail from the support tickets API.
 * @returns Detail payload for the support ticket content area.
 */
export function mapSupportTicketDetailFromApiResponse(
  ticket: SupportTicketDetailApiResponse,
): SupportTicketDetail {
  return {
    id: ticket.id,
    title: ticket.title,
    description: ticket.description?.trim() ?? "",
    details: {
      patientName: ticket.patient_name?.trim() || "--",
      mrn: ticket.patient_mrn?.trim() || "--",
      status: mapSupportTicketStatus(ticket.status),
      priority: ticket.priority || "--",
      created: formatSupportTicketTimestamp(ticket.created_at),
      updated: formatSupportTicketTimestamp(ticket.updated_at),
      openedBy: ticket.raised_by?.trim() || "--",
    },
    comments: buildSupportTicketThread(ticket.comments ?? []),
    attachments: (ticket.attachments ?? []).map(mapApiAttachment),
  };
}
